import {
  useNavigation,
  useNavigationState,
} from '../navigation/NavigationProvider';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { DialogContent } from '../../components/ui/DialogContent';
import { DialogActions } from '../../components/ui/DialogActions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { authService } from '../../services/authService';
import { AuthenticatedUser } from '../../domain/auth';

export class ReauthenticationCancelled extends Error {}

export const billingAuthenticationMessage = (
  error: unknown,
): string | undefined => {
  const value = error as { code?: string; details?: { code?: string } } | null;
  if (value?.details?.code === 'verification-required')
    return 'Verify your email address before opening Billing.';
  if (
    value?.details?.code === 'authentication-required' ||
    value?.code === 'functions/unauthenticated'
  ) {
    return 'Your session needs confirmation. Sign in again to continue.';
  }
  return undefined;
};

type Pending = {
  user: AuthenticatedUser;
  resolve: () => void;
  reject: (error: Error) => void;
};

export function useBillingReauthentication() {
  const { replaceState } = useNavigation();
  const [open, setOpen] = useNavigationState('billing.reauth', false);
  const pending = useRef<Pending | undefined>(undefined);
  const [user, setUser] = useState<AuthenticatedUser>();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const inFlight = useRef(false);
  const mounted = useRef(false);

  const cancel = useCallback(() => {
    pending.current?.reject(new ReauthenticationCancelled());
    pending.current = undefined;
    setUser(undefined);
    setPassword('');
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    mounted.current = true;
    const unsubscribe = authService.subscribe((current) => {
      if (pending.current && current?.uid !== pending.current.user.uid)
        cancel();
    });
    return () => {
      mounted.current = false;
      unsubscribe();
      pending.current?.reject(new ReauthenticationCancelled());
      pending.current = undefined;
    };
  }, [cancel]);

  useEffect(() => {
    if (!open && pending.current) cancel();
    if (open && !pending.current) replaceState('billing.reauth', false);
  }, [open, user, cancel, replaceState]);
  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey: 'billing.reauth',
    open: open && Boolean(user),
    dirty: false,
    pending: busy,
    onDiscard: () => {},
    onClose: cancel,
  });
  const dismiss = () => {
    void requestClose();
  };

  const run = async <T,>(action: () => Promise<T>): Promise<T> => {
    const actor = authService.currentUser();
    try {
      return await action();
    } catch (failure) {
      if (!mounted.current) throw new ReauthenticationCancelled();
      const value = failure as {
        code?: string;
        details?: { code?: string };
      } | null;
      if (
        value?.details?.code !== 'authentication-required' &&
        value?.code !== 'functions/unauthenticated'
      )
        throw failure;
      if (
        !actor ||
        authService.currentUser()?.uid !== actor.uid ||
        pending.current
      )
        throw failure;
      await new Promise<void>((resolve, reject) => {
        pending.current = { user: actor, resolve, reject };
        setPassword('');
        setError(undefined);
        setUser(actor);
        setOpen(true);
      });
      if (!mounted.current || authService.currentUser()?.uid !== actor.uid)
        throw new ReauthenticationCancelled();
      // Retry only the explicit authentication rejection, once, with the original request.
      return action();
    }
  };

  const confirm = async (method: 'password' | 'google.com') => {
    const request = pending.current;
    if (!request || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await authService.reauthenticate(request.user.uid, method, password);
      if (pending.current !== request) return;
      pending.current = undefined;
      setUser(undefined);
      setPassword('');
      runAfterSave(() => replaceState('billing.reauth', false));
      request.resolve();
    } catch (failure) {
      if (pending.current !== request) return;
      const code = (failure as { code?: string })?.code;
      setError(
        code === 'auth/popup-blocked'
          ? 'Allow popups for this site, then try again.'
          : code === 'auth/popup-closed-by-user' ||
              code === 'auth/cancelled-popup-request'
            ? 'Google confirmation was cancelled. Try again or cancel this action.'
            : 'Identity could not be confirmed. Check your password or Google account and try again. If your session expired, sign in again.',
      );
      setPassword('');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const dialog = (
    <ResponsiveDialog
      aria-labelledby="billing-reauth-title"
      open={open && Boolean(user)}
      onClose={dismiss}
      fullWidth
      maxWidth="xs"
    >
      <DialogHeader
        id="billing-reauth-title"
        title="Confirm your identity"
        onClose={dismiss}
        closeDisabled={busy}
      />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography>
            Confirm your identity to continue this billing action.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {user?.providerIds.includes('password') && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void confirm('password');
              }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={busy}
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={busy || !password}
                >
                  Confirm with password
                </Button>
              </Stack>
            </form>
          )}
          {user?.providerIds.includes('google.com') && (
            <Button
              disabled={busy}
              onClick={() => {
                void confirm('google.com');
              }}
            >
              Confirm with Google
            </Button>
          )}
          {user &&
            !user.providerIds.some(
              (id) => id === 'password' || id === 'google.com',
            ) && (
              <Alert severity="info">
                Sign in again using your account’s sign-in method, then retry
                this action.
              </Alert>
            )}
          {busy && <Typography role="status">Confirming identity…</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismiss} disabled={busy}>
          Cancel
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
  return { run, dialog };
}
